import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { calculateAscMC, longitudeToSign, SIGNS } from '../../services/ephemeris';

// ─── Sign emoji map ───────────────────────────────────────────
const SIGN_EMOJIS: Record<string, string> = {
  'Áries':      '♈',
  'Touro':      '♉',
  'Gêmeos':     '♊',
  'Câncer':     '♋',
  'Leão':       '♌',
  'Virgem':     '♍',
  'Libra':      '♎',
  'Escorpião':  '♏',
  'Sagitário':  '♐',
  'Capricórnio':'♑',
  'Aquário':    '♒',
  'Peixes':     '♓',
};

const SIGN_DESCRIPTIONS: Record<string, string> = {
  'Áries':       'Você projeta energia, iniciativa e confiança. As pessoas te percebem como direta, corajosa e sempre pronta para agir.',
  'Touro':       'Você passa uma imagem de estabilidade, confiabilidade e charme natural. As pessoas te percebem como paciente, sensual e com gosto refinado.',
  'Gêmeos':      'Você é percebido como curioso, comunicativo e versátil. As pessoas te veem como inteligente, divertido e sempre com algo interessante a dizer.',
  'Câncer':      'Você projeta acolhimento, sensibilidade e cuidado genuíno. As pessoas te percebem como empático, protetor e profundamente intuitivo.',
  'Leão':        'Você irradia magnetismo, criatividade e presença marcante. As pessoas te percebem como carismático, generoso e naturalmente liderante.',
  'Virgem':      'Você projeta competência, atenção aos detalhes e inteligência prática. As pessoas te percebem como confiável, analítico e orientado para a perfeição.',
  'Libra':       'Você transmite charme, elegância e diplomacia natural. As pessoas te percebem como equilibrado, estético e comprometido com a harmonia.',
  'Escorpião':   'Você projeta intensidade, profundidade e magnetismo misterioso. As pessoas te percebem como poderoso, perspicaz e com uma presença marcante.',
  'Sagitário':   'Você irradia otimismo, aventura e liberdade de espírito. As pessoas te percebem como filosófico, entusiasmado e sempre em busca do horizonte.',
  'Capricórnio': 'Você projeta autoridade, ambição e maturidade natural. As pessoas te percebem como responsável, determinado e com objetivos claros.',
  'Aquário':     'Você transmite originalidade, independência e visão de futuro. As pessoas te percebem como inovador, humanitário e ligeiramente excêntrico.',
  'Peixes':      'Você projeta sensibilidade, empatia e uma aura quase mística. As pessoas te percebem como artístico, espiritual e profundamente compassivo.',
};

// ─── JSON-LD schemas ──────────────────────────────────────────

const softwareAppSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Calculador de Ascendente',
  applicationCategory: 'LifestyleApplication',
  operatingSystem: 'Web',
  description: 'Calcule seu signo ascendente gratuitamente baseado na data, hora e local de nascimento.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'BRL' },
  provider: { '@type': 'Organization', name: 'Professya', url: 'https://professya.com' },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://professya.com' },
    { '@type': 'ListItem', position: 2, name: 'Ascendente', item: 'https://professya.com/ascendente' },
  ],
};

// ─── Geocode via Nominatim ────────────────────────────────────

interface NominatimResult {
  lat: string;
  lon: string;
  display_name: string;
}

async function geocodeCity(city: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json&limit=1`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'pt-BR,pt;q=0.9', 'User-Agent': 'Professya/1.0' },
    });
    if (!res.ok) return null;
    const data: NominatimResult[] = await res.json();
    if (!data.length) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {
    return null;
  }
}

// ─── Component ───────────────────────────────────────────────

interface CalcResult {
  sign: string;
  degree: number;
}

export function AscendantePage() {
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthCity, setBirthCity] = useState('');
  const [result, setResult] = useState<CalcResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!birthDate || !birthTime || !birthCity.trim()) {
      setError('Por favor, preencha todos os campos.');
      return;
    }

    setLoading(true);
    try {
      const coords = await geocodeCity(birthCity.trim());
      if (!coords) {
        setError('Não foi possível encontrar a cidade. Tente digitar de forma diferente ou adicionar o país (ex: "São Paulo, Brasil").');
        return;
      }

      const [year, month, day] = birthDate.split('-').map(Number);
      const [hour, minute] = birthTime.split(':').map(Number);
      const date = new Date(year, month - 1, day, hour, minute, 0);

      const { asc } = calculateAscMC(date, coords.lat, coords.lng);
      const { sign, degree } = longitudeToSign(asc);
      setResult({ sign, degree });
    } catch {
      setError('Ocorreu um erro ao calcular. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  const signEmoji = result ? (SIGN_EMOJIS[result.sign] ?? '✦') : '';
  const signDesc  = result ? (SIGN_DESCRIPTIONS[result.sign] ?? '') : '';

  return (
    <>
      <Helmet>
        <title>Calcule seu Signo Ascendente Grátis, Professya</title>
        <meta name="description" content="Descubra qual é o seu signo ascendente com nosso calculador gratuito. Saiba como o ascendente define sua aparência, primeira impressão e identidade pública." />
        <meta property="og:title" content="Calculador de Ascendente Grátis, Professya" />
        <meta property="og:description" content="Descubra qual é o seu signo ascendente com nosso calculador gratuito. Saiba como o ascendente define sua aparência, primeira impressão e identidade pública." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://professya.com/ascendente" />
        <link rel="canonical" href="https://professya.com/ascendente" />
        <script type="application/ld+json">{JSON.stringify(softwareAppSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-base)', color: 'var(--text-primary)' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(180deg, rgba(245,200,66,0.08) 0%, transparent 100%)', padding: '3rem 1.5rem 2rem', textAlign: 'center' }}>
          <Link to="/" style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', textDecoration: 'none', display: 'inline-block', marginBottom: '1.5rem' }}>
            ✦ Professya
          </Link>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>⬆️</div>
          <h1 className="font-display" style={{ fontSize: 'clamp(2rem,6vw,3rem)', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
            Calculador de Ascendente
          </h1>
          <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto', lineHeight: 1.7, fontSize: 'var(--text-body)' }}>
            Descubra seu signo ascendente gratuitamente, o ponto do zodíaco que estava nascendo no horizonte leste no momento do seu nascimento.
          </p>
        </div>

        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem 4rem' }}>

          {/* Explainer */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '0.875rem', color: 'var(--petrol)' }}>
              O que é o Ascendente?
            </h2>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85, marginBottom: '1rem' }}>
              O <strong style={{ color: 'var(--text-primary)' }}>signo ascendente</strong> (ou Ascendente) é o signo que estava surgindo no horizonte leste exatamente no momento do seu nascimento. Ele representa a sua <em>máscara social</em>, como você se apresenta ao mundo, a impressão que causa nos outros e as características que as pessoas percebem em você antes mesmo de te conhecerem bem.
            </p>
            <p style={{ color: 'var(--text-secondary)', lineHeight: 1.85 }}>
              Enquanto o signo solar revela quem você <em>é</em> no núcleo, o ascendente mostra quem você <em>parece ser</em>. Juntos, eles formam a base da sua identidade astrológica. Por isso, para calcular o ascendente com precisão, é necessário conhecer a hora e o local exatos do nascimento.
            </p>
          </section>

          {/* Calculator */}
          <section style={{ marginBottom: '2.5rem' }}>
            <div className="card" style={{ border: '1px solid var(--border-gold)', background: 'linear-gradient(135deg, rgba(245,200,66,0.04) 0%, transparent 100%)' }}>
              <h2 className="font-display" style={{ fontSize: '1.25rem', marginBottom: '1.5rem', textAlign: 'center' }}>
                ✦ Calcule seu Ascendente Grátis
              </h2>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Data de nascimento
                  </label>
                  <input
                    type="date"
                    value={birthDate}
                    onChange={e => setBirthDate(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-body)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Hora de nascimento
                  </label>
                  <input
                    type="time"
                    value={birthTime}
                    onChange={e => setBirthTime(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-body)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Cidade de nascimento
                    <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '0.5rem' }}>
                      (necessário para cálculo preciso)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={birthCity}
                    onChange={e => setBirthCity(e.target.value)}
                    placeholder="Ex: São Paulo, Brasil"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-md)',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--text-body)',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                {error && (
                  <p style={{ color: 'var(--color-error, #ef4444)', fontSize: 'var(--text-label)', margin: 0 }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ marginTop: '0.25rem' }}
                >
                  {loading ? 'Calculando...' : 'Calcular meu Ascendente ✦'}
                </button>
              </form>
            </div>
          </section>

          {/* Result */}
          {result && (
            <section style={{ marginBottom: '2.5rem' }}>
              <div className="card" style={{ textAlign: 'center', border: '1px solid var(--border-gold)', background: 'linear-gradient(135deg, rgba(245,200,66,0.08) 0%, transparent 100%)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>{signEmoji}</div>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', marginBottom: '0.5rem' }}>Seu Ascendente é</p>
                <h2 className="font-display" style={{ fontSize: 'clamp(1.75rem,5vw,2.5rem)', color: 'var(--petrol)', margin: '0 0 0.25rem' }}>
                  {result.sign}
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
                  {result.degree.toFixed(1)}°
                </p>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.8, marginBottom: '1.75rem', maxWidth: '480px', margin: '0 auto 1.75rem' }}>
                  {signDesc}
                </p>
                <Link to="/register" className="btn btn-primary">
                  Descubra seu Mapa Natal Completo →
                </Link>
              </div>
            </section>
          )}

          {/* Why it matters */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1rem' }}>
              Por que o Ascendente importa?
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
              {[
                { icon: '👁️', title: 'Primeira impressão', desc: 'Define como as pessoas te percebem antes de te conhecerem de verdade.' },
                { icon: '🧬', title: 'Aparência física', desc: 'Influencia traços físicos e o estilo natural de se vestir e se portar.' },
                { icon: '🏠', title: 'Casas astrológicas', desc: 'Determina a posição das 12 casas do seu mapa natal, organizando todas as áreas da vida.' },
                { icon: '🎭', title: 'Persona social', desc: 'Representa a máscara que você usa no mundo e sua identidade pública.' },
              ].map(item => (
                <div key={item.title} className="card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>{item.icon}</div>
                  <h3 style={{ fontSize: 'var(--text-label)', fontWeight: 600, marginBottom: '0.4rem' }}>{item.title}</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Signs overview */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1.375rem', marginBottom: '1rem' }}>
              Ascendentes e suas expressões
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.875rem' }}>
              {SIGNS.map(sign => (
                <div key={sign} className="card" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{SIGN_EMOJIS[sign]}</span>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: 'var(--text-label)', marginBottom: '0.25rem' }}>Ascendente em {sign}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', lineHeight: 1.6 }}>{SIGN_DESCRIPTIONS[sign]}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="card" style={{ textAlign: 'center', background: 'linear-gradient(135deg, rgba(245,200,66,0.06) 0%, transparent 100%)', border: '1px solid rgba(245,200,66,0.2)' }}>
            <p className="font-display" style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
              Quer entender seu mapa natal completo?
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>
              O ascendente é apenas o começo. Descubra como planetas, casas e aspectos se combinam para revelar o seu potencial único.
            </p>
            <Link to="/register" className="btn btn-primary">
              Descubra seu Mapa Natal Completo →
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
